/** Список подання (features/list) і панель екрана над ним. */
import { type NounUk, noun } from '@clientline/contracts/i18n';
import { hasForms, lowerFirst } from '../words.ts';

export const list = {
  todayToggle: 'Сьогодні',
  selectAll: 'Обрати всі завантажені',
  selectRow: 'Обрати запис',
  selected: 'Обрано',
  clearSelection: 'Зняти вибір',
  /** Набір записів змінився (LST-24): обрані могли випасти з нього. */
  /** Заголовок стовпця вибору без прав на дії — лише для програми читання з екрана. */
  selectColumn: 'Вибір',
  selectionReset: 'Вибір скинуто: змінились фільтри',
  /** Крок 2 вибору (LST-25): усі записи за фільтром, а не лише завантажені. */
  selectAllMatching: (n: string) => `Обрати всі ${n} за поточним фільтром`,
  selectAllMatchingUnknown: 'Обрати всі за поточним фільтром',
  /** Обрано всі за фільтром, а точного числа список не рахує (понад тисячу, LST-15). */
  allMatchingUnknown: 'усі за фільтром',
  /** Панель вибору з одним рядком (trash.html, кадр Б1): видалення в кошик з наслідками. */
  deleteOne: 'Видалити…',
  /** Кілька обраних чи «усі за фільтром» — масова задача в кошик (LST-31). */
  deleteMany: 'Видалити…',
  /** Масова зміна полів і відповідального (LST-27, LST-28). */
  editMany: 'Змінити поля…',
  transferMany: 'Змінити відповідального…',
  /** Експорт (LST-32): обраних — з панелі, усіх за фільтром — з панелі інструментів. */
  exportMany: 'Експорт…',
  exportAll: 'Експорт',
  bulkUnderSearch: 'Під пошуком — лише вибрані рядки',
  /** Обрано вручну понад межу id одного запиту (`MAX_BULK_SELECTION_IDS`). */
  bulkTooMany: 'Обрано забагато рядків: оберіть «усі за поточним фільтром» або зменшіть вибір',
  /** Нова масова дія чи експорт, поки попередня задача того самого виду в дорозі (#1027, S1). */
  bulkRunning: 'Попередня задача ще виконується — дочекайтеся її завершення',
  /** Кошик сутності після подань (trash.html, кадр В1): «Видалені» + множина сутності. */
  deletedView: (label: NounUk) =>
    hasForms(label) ? `Видалені ${lowerFirst(noun(label, { plural: true }))}` : 'Видалені записи',
  /** Черга дублів сутності після кошика (DAT-07): лише ролям з правом злиття. */
  duplicatesView: (label: NounUk) =>
    hasForms(label) ? `Дублі ${noun(label, { case: 'gen', plural: true })}` : 'Дублі записів',
  noStep: 'Немає кроку',
  call: 'Подзвонити',
  errorListTitle: 'Не вдалося завантажити список',
  /** Запасна кнопка в кінці завантаженого (LST-14). */
  loadMore: (n: number) => `Показати ще ${n}`,
  loadMoreFailed: 'Не вдалося завантажити далі',
  /** Наступна порція не вклалась у межу часу (LST-16). */
  loadMoreTooHeavy: 'Подання надто важке, щоб завантажити далі',
  /** Позначки живого оновлення (LST-18): рядок лишається на місці до оновлення. */
  rowRemoved: 'Видалено',
  rowGone: 'Недоступний',
  /** Лічильник подання (LST-15): межа часу підрахунку — «—» з поясненням. */
  countUnknown: 'Не вдалося порахувати: подання надто важке для підрахунку',
  countCapped: 'Більше тисячі записів: точне число не рахуємо, щоб не сповільнювати список',
  /** Кнопка вибору стовпців у панелі екрана (LST-02). */
  columns: 'Стовпці',
  /** Особистий вибір стовпців не дійшов до сервера (E4a-03): екран повертає збережене. */
  columnsNotSaved: 'Не вдалося зберегти стовпці. Спробуйте ще раз',
  /** Особисті ширини стовпців (LST-03) не дійшли до сервера. */
  widthsNotSaved: 'Не вдалося зберегти ширину стовпців',
  /**
   * Кнопка «↻ N»: нові записи не вставляються самі (LST-18). Форму слова дає `plural()` екрана:
   * format.ts сюди не імпортуємо — словник тягне й документ входу.
   */
  refreshChanges: (counted: string) => `Є ${counted} — оновити`,
  changeForms: { one: 'зміна', few: 'зміни', many: 'змін' },
  // ---------------------------------------------------------------- подання (FLT-06, FLT-07)
  /** Кнопка-назва подання в панелі: відкриває перемикач подань. */
  viewSwitcherHint: 'Інші подання',
  /** Кнопка меню дій з поданням (перейменувати, поділитися, видалити…). */
  viewActions: 'Дії з поданням',
  /** Чипи подань, що не вмістились у рядок панелі (ViewToolbar): «Ще · N» і що в ньому. */
  viewChipsMore: (n: number) => `Ще · ${n}`,
  viewChipsMoreLabel: (names: readonly string[]) => `Інші подання: ${names.join(', ')}`,
  /** Дії панелі, що не вмістились (ViewToolbar): кнопка «⋯» перед основною дією. */
  toolbarMoreLabel: (names: readonly string[]) =>
    names.length > 0 ? `Інші дії: ${names.join(', ')}` : 'Інші дії',
  /** Позначка незбережених змін поверх подання (FLT-07). */
  viewChanged: 'змінено',
  viewSave: 'Зберегти',
  viewSaveAs: 'Зберегти як нове',
  viewReset: 'Скинути',
  /** «Зберегти» без зв'язку: кнопка лишається видимою, підказка пояснює чому не діє. */
  viewSaveOffline: 'Немає зв’язку: зберегти можна, щойно він повернеться',
  /** `?d=` зіпсований чи з іншої версії (FLT-08): відкрито подання без змін. */
  viewLinkStale: 'Посилання на зміни подання застаріло: відкрито подання без змін',
  viewPin: 'Закріпити',
  viewUnpin: 'Відкріпити',
  viewRename: 'Перейменувати…',
  viewDuplicate: 'Дублювати…',
  viewVisibility: 'Видимість…',
  viewMakeDefault: 'Відкривати першим',
  viewUnsetDefault: 'Не відкривати першим',
  viewCopyLink: 'Копіювати посилання',
  viewDelete: 'Видалити',
  /** Лінивий модуль подань не завантажився (мережа, нова збірка). */
  viewToolsFailed: 'Не вдалося відкрити дії з поданням. Спробуйте ще раз',
  /** Межа завантажених рядків (LST-14): далі — фільтр або експорт. */
  listCapped: (n: number) =>
    `Показано ${n.toLocaleString('uk-UA')} записів. Уточніть фільтр або експортуйте подання`,
} as const;
